import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { 
  Trophy,
  MapPin,
  ExternalLink,
  MessageCircle
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_SHORT = {
  Keeps: "K", Drives: "R", Referees: "F", Coaches: "C", Coacht: "C", Rijdt: "R", Activity: "A", Unavailable: "N/A",
};

const TASK_LABELS = {
  Keeps: "Keept", Drives: "Rijdt", Referees: "Fluit", Coaches: "Coacht", Coacht: "Coacht", Rijdt: "Rijdt", Activity: "Activiteit", Unavailable: "Afwezig",
};

const TASK_COLORS = {
  Keeps: { bg: "bg-blue-500", text: "text-blue-700", light: "bg-blue-50", border: "border-blue-200" },
  Drives: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Referees: { bg: "bg-purple-500", text: "text-purple-700", light: "bg-purple-50", border: "border-purple-200" },
  Coaches: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Coacht: { bg: "bg-emerald-500", text: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-200" },
  Rijdt: { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-50", border: "border-amber-200" },
  Activity: { bg: "bg-indigo-500", text: "text-indigo-700", light: "bg-indigo-50", border: "border-indigo-200" },
  Unavailable: { bg: "bg-red-500", text: "text-red-700", light: "bg-red-50", border: "border-red-200" },
};

const LEGEND_ITEMS = [
  { key: "Keeps", short: "K", label: "Keept" },
  { key: "Drives", short: "R", label: "Rijdt" },
  { key: "Referees", short: "F", label: "Fluit" },
  { key: "Coaches", short: "C", label: "Coacht" },
  { key: "Activity", short: "A", label: "Activiteit" },
];

const parseTasks = (taskString) => {
  if (!taskString || taskString === "-" || taskString === "Unavailable") return [];
  return taskString.split(", ").filter(t => t && t !== "-");
};

const TaskCell = ({ task }) => {
  if (task === "Unavailable") {
    return (
      <div className="w-full h-6 rounded bg-red-500 flex items-center justify-center">
        <span className="text-[10px] text-white font-medium">N/A</span>
      </div>
    );
  }
  
  const tasks = parseTasks(task);
  
  if (tasks.length === 0) {
    return <span className="text-slate-300 text-xs">–</span>;
  }
  
  if (tasks.length === 1) {
    const colors = TASK_COLORS[tasks[0]] || TASK_COLORS.Keeps;
    return (
      <div className={cn("w-full h-6 rounded flex items-center justify-center", colors.bg)}>
        <span className="text-[10px] font-semibold text-white">{TASK_SHORT[tasks[0]]}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center gap-0.5 h-6">
      {tasks.map((t) => {
        const colors = TASK_COLORS[t] || TASK_COLORS.Keeps;
        return (
          <div 
            key={t}
            className={cn("h-5 flex-1 flex items-center justify-center rounded-sm", colors.bg)}
          >
            <span className="text-[9px] font-bold text-white">{TASK_SHORT[t]}</span>
          </div>
        );
      })}
    </div>
  );
};

export const PublicOverview = () => {
  const { token } = useParams();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addressModal, setAddressModal] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    try {
      const response = await axios.get(`${API}/overview/${token}`);
      setOverview(response.data);
    } catch (error) {
      setError("Dit planningsoverzicht is niet beschikbaar of de link is verlopen.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Overzicht Niet Gevonden</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!overview) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-overview-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-blue-600">
                {overview.season.club_name}
              </p>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'var(--font-display)' }}>
                {overview.season.team_name} - {overview.season_part?.name}
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg p-3 border border-slate-200">
          <span className="text-xs text-slate-500">Legenda:</span>
          {LEGEND_ITEMS.map(item => {
            const colors = TASK_COLORS[item.key];
            return (
              <div key={item.key} className="flex items-center gap-1.5">
                <div className={cn("w-5 h-5 rounded flex items-center justify-center", colors.bg)}>
                  <span className="text-white text-xs font-bold">{item.short}</span>
                </div>
                <span className="text-xs text-slate-600">{item.label}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded bg-red-500 flex items-center justify-center">
              <span className="text-white text-[9px] font-bold">N/A</span>
            </div>
            <span className="text-xs text-slate-600">Afwezig</span>
          </div>
        </div>

        {/* Overview Table */}
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50" style={{ position: "sticky", left: 0, zIndex: 20, minWidth: 80 }}>Datum</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 sticky-col-tu-header" style={{ minWidth: 56 }}>T/U</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap bg-slate-50 border-r border-slate-200 sticky-col-teg-header" style={{ minWidth: 128 }}>Tegenstander</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap">Team</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap">Locatie</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap">Tenue</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap">Verzamelen</th>
                <th className="text-left text-xs uppercase font-medium text-slate-500 tracking-wider px-3 py-3 whitespace-nowrap">Aanvang</th>
                {overview.players.map(player => {
                  const label = (player.last_name || player.first_name || player.name || "").trim();
                  const short = label.length > 8 ? `${label.substring(0, 8)}…` : label;
                  return (
                    <th
                      key={player.id}
                      data-testid={`public-overview-player-header-${player.id}`}
                      className="text-center text-xs uppercase font-medium text-slate-500 tracking-wider px-2 py-3 whitespace-nowrap"
                      style={{ minWidth: 72, maxWidth: 72, width: 72 }}
                      title={player.name}
                    >
                      {short}
                    </th>
                  );
                })}
                {(overview.managers || []).length > 0 && (
                  <th className="text-center text-xs uppercase font-medium text-slate-400 tracking-wider px-1 py-3 whitespace-nowrap border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></th>
                )}
                {(overview.managers || []).map(mgr => (
                  <th key={mgr.user_id} className="text-center text-xs uppercase font-medium text-emerald-600 tracking-wider px-2 py-3 whitespace-nowrap" style={{ minWidth: 72, maxWidth: 72, width: 72 }}>
                    {(mgr.name || "").split(" ")[0].substring(0, 8)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {overview.matches.map((row, index) => {
                if (row.type === "activity") {
                  return (
                    <tr key={row.match_id} data-testid={`public-overview-row-${index}`} style={{ backgroundColor: "#f5f3ff" }}>
                      <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: "#f5f3ff", minWidth: 80 }}>
                        {row.date ? format(new Date(row.date), "dd MMM") : "-"}
                      </td>
                      <td colSpan={2} className="px-3 py-3 text-sm font-medium text-indigo-700 whitespace-nowrap truncate border-r border-slate-100" style={{ position: "sticky", left: 80, zIndex: 10, backgroundColor: "#f5f3ff", maxWidth: 184 }}>
                        {row.description}
                      </td>
                      <td style={{ backgroundColor: "#f5f3ff" }}></td>
                      <td style={{ backgroundColor: "#f5f3ff" }}></td>
                      <td style={{ backgroundColor: "#f5f3ff" }}></td>
                      <td style={{ backgroundColor: "#f5f3ff" }}></td>
                      <td className="px-3 py-3 text-sm text-indigo-600 font-medium whitespace-nowrap" style={{ backgroundColor: "#f5f3ff" }}>
                        {row.time || ""}
                      </td>
                      {(row.tasks || []).map(task => (
                        <td key={task.player_id} className="px-2 py-3" style={{ backgroundColor: "#f5f3ff" }}>
                          <TaskCell task={task.task} />
                        </td>
                      ))}
                      {(overview.managers || []).length > 0 && (
                        <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4, backgroundColor: "#f5f3ff" }}></td>
                      )}
                      {(row.manager_tasks || []).map(mt => (
                        <td key={mt.user_id} className="px-2 py-3" style={{ backgroundColor: "#f5f3ff" }}>
                          <TaskCell task={mt.task} />
                        </td>
                      ))}
                    </tr>
                  );
                }

                const match = row;
                const bg = match.is_home ? "#ffffff" : "#f8fafc";
                const bgClass = match.is_home ? "bg-white" : "bg-slate-50/50";
                return (
                <tr 
                  key={match.match_id}
                  className={bgClass}
                  data-testid={`public-overview-row-${index}`}
                >
                  <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap" style={{ position: "sticky", left: 0, zIndex: 10, backgroundColor: bg, minWidth: 80 }}>
                    {match.date ? format(new Date(match.date), "dd MMM") : "-"}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap sticky-col-tu" style={{ backgroundColor: bg, minWidth: 56 }}>
                    <span className={cn("text-xs font-medium", match.is_home ? "text-emerald-700" : "text-amber-700")}>
                      {match.is_home ? "Thuis" : "Uit"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap truncate border-r border-slate-100 sticky-col-teg" style={{ backgroundColor: bg, minWidth: 128, maxWidth: 128 }}>
                    {match.opponent_club}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-500 whitespace-nowrap">
                    {match.opponent_team || "-"}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {match.address ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                        onClick={() => setAddressModal(match.address)}
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Bekijk
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={cn("text-xs font-medium", match.shirt === "Home" ? "text-emerald-700" : "text-amber-700")}>
                      {match.shirt === "Home" ? "Thuis" : match.shirt === "Away" ? "Uit" : match.shirt}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap">
                    {match.gather_time || <span className="text-slate-400 italic">ntb</span>}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700 whitespace-nowrap">
                    {match.start_time || <span className="text-slate-400 italic">ntb</span>}
                  </td>
                  {match.tasks.map(task => (
                    <td key={task.player_id} className="px-2 py-3">
                      <TaskCell task={task.task} />
                    </td>
                  ))}
                  {(overview.managers || []).length > 0 && (
                    <td className="border-l-2 border-slate-300" style={{ minWidth: 4, maxWidth: 4, width: 4 }}></td>
                  )}
                  {(match.manager_tasks || []).map(mt => (
                    <td key={mt.user_id} className="px-2 py-3">
                      <TaskCell task={mt.task} />
                    </td>
                  ))}
                </tr>
              )})}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-slate-400">
            Powered by Sportly Hockey Planner
          </p>
        </div>
      </div>

      {/* Address Modal */}
      <Dialog open={!!addressModal} onOpenChange={() => setAddressModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wedstrijdlocatie</DialogTitle>
            <DialogDescription>Clubadres details</DialogDescription>
          </DialogHeader>
          {addressModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Straat:</span>{" "}
                  <span className="text-slate-900">{addressModal.street || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Postcode:</span>{" "}
                  <span className="text-slate-900">{addressModal.postal_code || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Plaats:</span>{" "}
                  <span className="text-slate-900">{addressModal.city || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Land:</span>{" "}
                  <span className="text-slate-900">{addressModal.country || "N/B"}</span>
                </p>
              </div>
              {addressModal.google_maps_url && (
                <Button
                  className="w-full"
                  onClick={() => window.open(addressModal.google_maps_url, "_blank")}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Bekijk op Google Maps
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback link */}
      <div className="text-center py-4">
        <button
          onClick={() => setShowFeedback(true)}
          className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1.5"
          data-testid="public-feedback-link"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Fout of idee melden?
        </button>
      </div>

      <FeedbackDialog open={showFeedback} onOpenChange={setShowFeedback} />
    </div>
  );
};
